'use client';

import { ReactNode, useEffect, useState } from 'react';
import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { Btn } from '../Btn';
import { Nav, StepHead, StepWrap } from '../StepShell';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
};

function genId() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 8; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
}

export function Step11Publish({ data, setData, onBack }: Props) {
  const [id] = useState(genId);
  const [copied, setCopied] = useState(false);
  const isEmbedded = data.deploymentMode === 'embedded';

  // Persist locally so Phase 2 (Participant) can read by ID later.
  useEffect(() => {
    try {
      window.localStorage.setItem(`study:${id}`, JSON.stringify(data));
    } catch {
      /* localStorage may be blocked */
    }
  }, [id, data]);

  const link =
    typeof window !== 'undefined'
      ? `${window.location.origin}/study/${id}`
      : `https://ai-interviewer.app/study/${id}`;

  const copy = () => {
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <StepWrap>
      <StepHead
        label="Step 12 — Publish"
        title="Your study is ready"
        subtitle="Share the link below with participants. Each session will be recorded and transcribed automatically."
      />

      <div
        style={{
          background: T.white,
          border: `1.5px solid ${T.creamBorder}`,
          borderRadius: 14,
          padding: '24px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: T.espressoLight,
          }}
        >
          Participant Link
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div
            style={{
              flex: 1,
              background: T.cream,
              border: `1px solid ${T.creamBorder}`,
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 14,
              color: T.terra,
              fontWeight: 500,
              fontFamily: 'monospace',
              letterSpacing: '0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {link}
          </div>
          <Btn onClick={copy} variant={copied ? 'sage' : 'primary'}>
            {copied ? '✓ Copied!' : 'Copy Link'}
          </Btn>
        </div>
      </div>

      {isEmbedded && (
        <div
          style={{
            background: T.white,
            border: `1.5px solid ${T.creamBorder}`,
            borderRadius: 14,
            padding: '24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: T.espressoLight,
            }}
          >
            Return URL
          </div>
          <div style={{ fontSize: 14, color: T.espressoMid, lineHeight: 1.55 }}>
            After the interview, the participant will be redirected back to this
            URL. In Qualtrics, use the &ldquo;End of Survey&rdquo; element&apos;s
            redirect URL, or paste your survey&apos;s next-page link here.
          </div>
          <input
            value={data.redirectUrl}
            onChange={(e) => setData({ ...data, redirectUrl: e.target.value })}
            placeholder="https://yourorg.qualtrics.com/jfe/form/SV_xxx?…"
            style={{
              width: '100%',
              border: `1.5px solid ${T.creamBorder}`,
              borderRadius: 10,
              padding: '12px 14px',
              fontFamily: 'monospace',
              fontSize: 13,
              color: T.espresso,
              background: T.cream,
              outline: 'none',
              transition: 'border-color 0.15s',
            }}
            onFocus={(e) => (e.target.style.borderColor = T.terra)}
            onBlur={(e) => (e.target.style.borderColor = T.creamBorder)}
          />
          {!data.redirectUrl.trim() && (
            <div style={{ fontSize: 12, color: T.danger }}>
              Required for embedded studies — participants won&apos;t be able to
              return to your survey without this.
            </div>
          )}
        </div>
      )}

      <div
        style={{
          background: T.white,
          border: `1.5px solid ${T.creamBorder}`,
          borderRadius: 14,
          padding: '24px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: T.espressoLight,
          }}
        >
          Study Summary
        </div>

        <SummarySection label="Study Goal">
          <p style={{ fontSize: 15, lineHeight: 1.6 }}>{data.goal}</p>
        </SummarySection>

        <SummarySection label="Research Interests">
          <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.interests.map((item, i) => (
              <li key={i} style={{ fontSize: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: T.terra, fontWeight: 600 }}>{i + 1}.</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SummarySection>

        <SummarySection label={`Interview Questions (${data.questions.length})`}>
          <ol style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.questions.map((q, i) => (
              <li key={q.id} style={{ fontSize: 14, lineHeight: 1.55, display: 'flex', gap: 10 }}>
                <span style={{ color: T.terra, fontWeight: 600, minWidth: 22 }}>{i + 1}.</span>
                <span>{q.text}</span>
              </li>
            ))}
          </ol>
        </SummarySection>

        {data.background && (
          <SummarySection label="Background">
            <p style={{ fontSize: 14, lineHeight: 1.6, color: T.espressoMid }}>{data.background}</p>
          </SummarySection>
        )}
      </div>

      <div
        style={{
          background: T.sageLight,
          border: `1px solid ${T.sage}`,
          borderRadius: 10,
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          fontSize: 14,
          color: T.espressoMid,
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: T.espresso }}>Data collection</strong>
        Each session will automatically generate a full transcript, audio recording, and per-question response summary.
        Access your dashboard at{' '}
        <span style={{ color: T.terra, fontWeight: 500 }}>ai-interviewer.app/dashboard</span>
      </div>

      <Nav onBack={onBack} onNext={null} nextLabel="Publish" />
    </StepWrap>
  );
}

function SummarySection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          fontSize: 12,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: T.espressoLight,
        }}
      >
        {label}
      </div>
      <div style={{ borderLeft: `3px solid ${T.creamBorder}`, paddingLeft: 14 }}>{children}</div>
    </div>
  );
}
